import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  approveAllNetworkMembers,
  approveNetworkMember,
  assignStoreServes,
  declineNetworkMember,
  getStatus,
  joinByPrimaryUrl,
  leaveNetwork,
  listRules,
  listStores,
  publishConfig,
  publishToAllStores,
  shopifyAppInstallMailto,
  shopifyAppInstallUrl,
  toastPublishResult,
  updateSettings,
  type NetworkStore,
} from "../lib/safegeo-api";
import {
  isFreePlan,
  planLabel,
  storefrontHost,
  storeLimitForPlan,
} from "../lib/format";
import { fieldValue } from "../lib/polaris";
import { queryKeys } from "../lib/query-keys";
import { UpgradeNowButton } from "../components/UpgradeNowButton";
import { AppButton } from "../components/AppButton";
import { AppLink } from "../components/AppLink";
import { SafePage } from "../components/SafePage";
import { PhaseBadge, phaseFromStatus } from "../components/PhaseBadge";
import { SaveBarHost } from "../components/SaveBarHost";
import { PageLoading } from "../components/PageLoading";
import {
  ErrorBanner,
  InfoBanner,
  WarningBanner,
} from "../components/PageBanner";
import { CountryMultiSelect } from "../components/CountryMultiSelect";
import { VisitorModeSection } from "../components/VisitorModeSection";
import { WebsiteRulesSection } from "../components/WebsiteRulesSection";
import { PreviewAsVisitor } from "../components/PreviewAsVisitor";
import { countryName } from "../lib/countries";
import { showToast } from "../lib/feedback";
import { useSaveBar } from "../lib/save-bar";

const MODE_SAVE_ID = "safegeo-store-redirect-mode";
const APP_HANDLE = import.meta.env.VITE_SHOPIFY_APP_HANDLE || "safegeo-1";

function defaultStorefrontUrl(domain?: string, existing?: string | null) {
  if (existing?.trim()) return existing.trim();
  if (!domain) return "";
  const host = domain.replace(/^https?:\/\//i, "").replace(/\/$/, "");
  return host ? `https://${host}` : "";
}

/**
 * Store redirect (v3.2 A5–A7):
 * Free → locked website unlock + join secondary
 * Starter+ → websites + ask/auto; Multi/Scale → + network
 */
export function StoresPage() {
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [showConnectFlow, setShowConnectFlow] = useState(false);
  /** Free/Starter: join via primary URL only (no code field on main page). */
  const [showJoinUrl, setShowJoinUrl] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [servesDraft, setServesDraft] = useState<string[]>([]);
  const [urlDraft, setUrlDraft] = useState("");
  const [draftAuto, setDraftAuto] = useState<boolean | null>(null);
  const [rolePicked, setRolePicked] = useState(() => {
    try {
      return localStorage.getItem("sg_store_role_picked") === "1";
    } catch {
      return false;
    }
  });
  const [roleChoice, setRoleChoice] = useState<"primary" | "secondary">(
    "primary",
  );
  const statusQ = useQuery({ queryKey: queryKeys.status, queryFn: getStatus });
  const rulesQ = useQuery({ queryKey: queryKeys.rules, queryFn: listRules });
  const unlocked = !!statusQ.data?.multi_store_unlocked;
  const linkedAsSecondary = !!statusQ.data?.primary_shop_id;
  /** This shop requested to join - not “Primary has pending members”. */
  const awaitingPrimaryApproval = !!statusQ.data?.network_pending;
  const free = isFreePlan(statusQ.data?.plan, statusQ.data?.plan_key);
  const savedAuto = !!statusQ.data?.settings?.markets_auto;
  const marketsAuto = draftAuto ?? savedAuto;
  const modeDirty = draftAuto != null && draftAuto !== savedAuto;
  useSaveBar(MODE_SAVE_ID, modeDirty && !free);
  const customCount = (rulesQ.data?.items ?? []).filter(
    (r) => !r.is_fallback,
  ).length;
  const rulesMax = statusQ.data?.entitlements?.rules_max ?? 0;
  const atRulesMax = customCount >= rulesMax && rulesMax > 0;

  const storesQ = useQuery({
    queryKey: queryKeys.stores,
    queryFn: listStores,
    enabled:
      !!statusQ.data &&
      (unlocked || linkedAsSecondary || awaitingPrimaryApproval),
  });

  const status = statusQ.data;
  const isSecondary = linkedAsSecondary || awaitingPrimaryApproval;
  const isPrimary =
    typeof storesQ.data?.is_primary === "boolean"
      ? storesQ.data.is_primary
      : unlocked && !isSecondary;
  const pending = awaitingPrimaryApproval || !!storesQ.data?.pending;
  const network: NetworkStore[] = storesQ.data?.items || [];
  const shopDomain = status?.shop_domain || "";
  const planName = planLabel(status?.plan || status?.plan_key);
  const storeLimit = storeLimitForPlan(status?.plan, status?.plan_key);
  const connectedCount = Math.max(
    network.length,
    unlocked && isPrimary ? 1 : 0,
  );
  const atStoreLimit =
    storeLimit != null && connectedCount >= storeLimit;

  const phase = phaseFromStatus({
    published: !!status?.last_published_at,
    paused: !!status?.settings?.paused,
  });

  /** v9: Add rule only from Starter+ (websites). Free has no primary CTA. */
  const storesPrimary = free ? null : (
    <AppButton
      slot="primary-action"
      to={atRulesMax ? "/app/billing" : "/app/stores/new"}
    >
      {atRulesMax ? "Rule limit reached" : "Add rule"}
    </AppButton>
  );

  const refresh = () => void qc.invalidateQueries({ queryKey: ["safegeo"] });

  async function run(fn: () => Promise<void>, okToast?: string) {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      if (okToast) showToast(okToast);
      refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function embedBadge(row: NetworkStore) {
    if (row.embed_active === false) {
      return { tone: "warning" as const, label: "Embed off" };
    }
    return { tone: "success" as const, label: "Embed on" };
  }

  async function joinPrimary(rawInput: string) {
    const raw = rawInput.trim();
    // Spec 6.4.1: URL only (no linking code on primary page).
    const r = await joinByPrimaryUrl(raw);
    showToast(
      r.message ||
        (r.pending
          ? "Request sent - waiting for Primary approval"
          : "Store connected"),
    );
    setCodeInput("");
  }

  async function saveMode() {
    if (!modeDirty || draftAuto == null) return;
    setBusy(true);
    try {
      // Store redirect Ask/Auto shares markets_auto; also keep Market engine in sync.
      await updateSettings({ markets_auto: draftAuto });
      let published = null;
      try {
        published = await publishConfig();
      } catch {
        /* settings saved */
      }
      setDraftAuto(null);
      const pub = toastPublishResult(
        published,
        draftAuto
          ? "Visitors will be sent automatically"
          : "Visitors will be asked first",
      );
      if (!pub.ok && pub.message) setErr(pub.message);
      refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (statusQ.isLoading) {
    return (
      <SafePage
      screen="stores"
      heading="Store redirect"
      accessory={<PhaseBadge phase={phase} />}
      primaryAction={storesPrimary}
    >
        <PageLoading label="Loading…" />
      </SafePage>
    );
  }

  // —— Free: locked Store redirect (v3.2 A6 / A14 downgrade) ——
  if (free && !isSecondary) {
    return (
      <SafePage
      screen="stores"
      heading="Store redirect"
      accessory={<PhaseBadge phase={phase} />}
      primaryAction={storesPrimary}
    >
{customCount > 0 ? (
          <s-banner tone="warning" heading="Paused · Starter">
            You have {customCount} website rule
            {customCount === 1 ? "" : "s"} saved from a paid plan. They stay
            here but don’t run on Free.{" "}
            <UpgradeNowButton plan="starter" variant="secondary" />
          </s-banner>
        ) : null}
        <s-section heading="Unlock Store redirect">
          <s-stack direction="block" gap="base">
            <s-text type="strong">UK visitor → yourbrand.co.uk</s-text>
            <s-unordered-list>
              <s-list-item>
                Send visitors to another website you own (Starter)
              </s-list-item>
              <s-list-item>Ask first or send automatically</s-list-item>
              <s-list-item>
                Shoppers with items in their cart are always asked
              </s-list-item>
              <s-list-item>
                Multi-store connects up to 4 Shopify shops so each country can
                land on the right one
              </s-list-item>
            </s-unordered-list>
            <s-stack direction="inline" gap="base">
              <UpgradeNowButton plan="starter" />
              <AppButton to="/app/plans" variant="tertiary">
                See Multi-store
              </AppButton>
            </s-stack>
          </s-stack>
        </s-section>
        {showJoinUrl ? (
          <s-section heading="Join your primary store">
            {err ? (
              <ErrorBanner heading="Couldn’t connect">{err}</ErrorBanner>
            ) : null}
            <s-text-field
              label="Your primary store’s URL"
              value={codeInput}
              onChange={(e: Event) => setCodeInput(fieldValue(e))}
              details="brand.com or brand.myshopify.com"
            />
            <s-box paddingBlockStart="base">
              <s-stack direction="inline" gap="base">
                <s-button
                  variant="primary"
                  disabled={busy || !codeInput.trim()}
                  loading={busy}
                  onClick={() => void run(() => joinPrimary(codeInput))}
                >
                  Send request
                </s-button>
                <s-button
                  variant="tertiary"
                  onClick={() => setShowJoinUrl(false)}
                >
                  Cancel
                </s-button>
              </s-stack>
            </s-box>
          </s-section>
        ) : (
          <s-section heading="Part of another store’s setup?">
            <s-paragraph color="subdued">
              Join that shop as a secondary store - no extra plan on this store.
            </s-paragraph>
            <s-box paddingBlockStart="base">
              <s-button
                variant="tertiary"
                onClick={() => setShowJoinUrl(true)}
              >
                Join as a secondary store
              </s-button>
            </s-box>
          </s-section>
        )}
      </SafePage>
    );
  }

  // —— Starter (not Multi): websites + join secondary (v3.2 A7 Starter) ——
  if (!unlocked && !isSecondary) {
    return (
      <SafePage
      screen="stores"
      heading="Store redirect"
      accessory={<PhaseBadge phase={phase} />}
      primaryAction={storesPrimary}
    >
        <SaveBarHost
          id={MODE_SAVE_ID}
          saving={busy}
          onSave={() => void saveMode()}
          onDiscard={() => setDraftAuto(null)}
        />
{err ? <ErrorBanner heading="Couldn’t update">{err}</ErrorBanner> : null}
        <VisitorModeSection
          variant="store"
          marketsAuto={marketsAuto}
          free={false}
          busy={busy}
          onAsk={() => setDraftAuto(false)}
          onAuto={() => setDraftAuto(true)}
        />
        <s-section heading="My stores">
          <s-box padding="base" borderWidth="base" borderRadius="base">
            <s-stack direction="block" gap="small">
              <s-badge tone="success">This store · Primary</s-badge>
              <s-text type="strong">
                {storefrontHost({
                  primaryDomain: status?.primary_domain,
                  shopDomain: status?.shop_domain,
                })}
              </s-text>
              <s-paragraph color="subdued">
                Everyone else stays here · Same page
              </s-paragraph>
            </s-stack>
          </s-box>
        </s-section>
        <WebsiteRulesSection atRulesMax={atRulesMax} />
        <s-section heading="Connect your other Shopify stores">
          <s-paragraph color="subdued">
            Multi-store links up to 4 shops so each country can land on the
            right one.
          </s-paragraph>
          <s-box paddingBlockStart="base">
            <s-stack direction="inline" gap="base" alignItems="center">
              <s-badge tone="info">Multi-store</s-badge>
              <UpgradeNowButton plan="multi" />
            </s-stack>
          </s-box>
        </s-section>
        {showJoinUrl ? (
          <s-section heading="Join your primary store">
            <s-text-field
              label="Your primary store’s URL"
              value={codeInput}
              onChange={(e: Event) => setCodeInput(fieldValue(e))}
              details="brand.com or brand.myshopify.com"
            />
            <s-box paddingBlockStart="base">
              <s-stack direction="inline" gap="base">
                <s-button
                  variant="primary"
                  disabled={busy || !codeInput.trim()}
                  loading={busy}
                  onClick={() => void run(() => joinPrimary(codeInput))}
                >
                  Send request
                </s-button>
                <s-button
                  variant="tertiary"
                  onClick={() => setShowJoinUrl(false)}
                >
                  Cancel
                </s-button>
              </s-stack>
            </s-box>
          </s-section>
        ) : (
          <s-section heading="Part of another store’s setup?">
            <s-paragraph color="subdued">
              Join that shop as a secondary store - no extra plan on this store.
            </s-paragraph>
            <s-box paddingBlockStart="base">
              <s-button
                variant="tertiary"
                onClick={() => setShowJoinUrl(true)}
              >
                Join as a secondary store
              </s-button>
            </s-box>
          </s-section>
        )}
        <PreviewAsVisitor
          context="store"
          prefer={status?.settings?.market_countries || undefined}
        />
      </SafePage>
    );
  }

  // —— Shared table helpers ——
  const rows: NetworkStore[] =
    network.length > 0
      ? network
      : [
          {
            shop_id: "self",
            shop_domain: shopDomain,
            role: isSecondary ? "secondary" : "primary",
            plan: status?.plan || "free",
            status: pending ? "pending" : "live",
            serves_countries: [],
            embed_active: status?.embed_active !== false,
          },
        ];

  function statusBadge(statusRaw: string) {
    const s = (statusRaw || "").toLowerCase();
    if (s === "pending")
      return { tone: "attention" as const, label: "Pending" };
    if (s === "live" || s === "active")
      return { tone: "success" as const, label: "Connected" };
    if (s === "grace") return { tone: "warning" as const, label: "Grace" };
    return { tone: "info" as const, label: statusRaw || "—" };
  }

  const primaryDomain =
    network.find((r) => r.role === "primary")?.shop_domain ||
    (isPrimary ? shopDomain : null);

  // —— 2) Connected secondary (or pending join) ——
  if (isSecondary && !isPrimary) {
    return (
      <SafePage
      screen="stores"
      heading="Store redirect"
      accessory={<PhaseBadge phase={phase} />}
      primaryAction={storesPrimary}
    >
{err ? (
          <ErrorBanner heading="Couldn’t update">{err}</ErrorBanner>
        ) : null}

        {awaitingPrimaryApproval ? (
          <InfoBanner heading="Waiting for approval">
            Your Primary Store still needs to approve this connection.
          </InfoBanner>
        ) : null}

        <s-section heading="Primary Store">
          <s-paragraph>
            <s-text type="strong">{primaryDomain || "Your Primary Store"}</s-text>
          </s-paragraph>
          <s-paragraph color="subdued">
            Where visitors go is managed on the Primary Store. You can cancel
            this store’s paid plan if it’s covered there.
          </s-paragraph>
          {primaryDomain ? (
            <s-box paddingBlockStart="base">
              <s-stack direction="inline" gap="base">
                <s-button
                  variant="secondary"
                  href={`https://${primaryDomain.replace(/^https?:\/\//i, "")}/admin/apps`}
                  target="_blank"
                >
                  Open {primaryDomain} admin
                </s-button>
                <AppButton variant="tertiary" to="/app/plans">
                  Change plan
                </AppButton>
              </s-stack>
            </s-box>
          ) : null}
        </s-section>

        <s-section heading="Connected stores">
          <s-table>
            <s-table-header-row>
              <s-table-header listSlot="primary">Store</s-table-header>
              <s-table-header>Role</s-table-header>
              <s-table-header>Status</s-table-header>
              <s-table-header>App embed</s-table-header>
            </s-table-header-row>
            <s-table-body>
              {rows.map((row) => {
                const badge = statusBadge(String(row.status || ""));
                const emb = embedBadge(row);
                return (
                  <s-table-row key={row.shop_id}>
                    <s-table-cell>{row.shop_domain}</s-table-cell>
                    <s-table-cell>
                      {row.role === "primary" ? "Primary Store" : "Connected"}
                    </s-table-cell>
                    <s-table-cell>
                      <s-badge tone={badge.tone}>{badge.label}</s-badge>
                    </s-table-cell>
                    <s-table-cell>
                      <s-badge tone={emb.tone}>{emb.label}</s-badge>
                    </s-table-cell>
                  </s-table-row>
                );
              })}
            </s-table-body>
          </s-table>
        </s-section>

        <s-section heading="Disconnect store">
          <s-paragraph color="subdued">
            Removes this shop from the Primary Store. Cross-store routing stops
            here; you can join again later with a new Store linking code.
          </s-paragraph>
          <s-button
            variant="tertiary"
            tone="critical"
            disabled={busy}
            onClick={() => {
              if (
                !confirm(
                  "Disconnect this store from the Primary Store? Cross-store routing will stop for this shop.",
                )
              ) {
                return;
              }
              void run(async () => {
                await leaveNetwork();
              }, "This store was disconnected");
            }}
          >
            Disconnect store
          </s-button>
        </s-section>

        
      </SafePage>
    );
  }

  // —— Role picker (A5) before Multi primary UI ——
  // Skip once network has peers or merchant already acknowledged Primary.
  if (
    unlocked &&
    !isSecondary &&
    !rolePicked &&
    network.length <= 1
  ) {
    return (
      <SafePage
      screen="stores"
      heading="Store redirect"
      accessory={<PhaseBadge phase={phase} />}
      primaryAction={storesPrimary}
    >
<s-section heading="What is this store’s role?">
          <s-paragraph color="subdued">
            Every business has one primary store and can connect other stores to
            it.
          </s-paragraph>
          <s-stack gap="base">
            <s-clickable
              padding="base"
              borderWidth="base"
              borderRadius="base"
              onClick={() => setRoleChoice("primary")}
            >
              <s-stack gap="small">
                <s-stack direction="inline" gap="small" alignItems="center">
                  <s-text type="strong">Primary store</s-text>
                  <s-badge tone="info">Recommended</s-badge>
                  {roleChoice === "primary" ? (
                    <s-badge tone="success">Selected</s-badge>
                  ) : null}
                </s-stack>
                <s-paragraph color="subdued">
                  This is my main store. I’ll connect my other stores here.
                </s-paragraph>
              </s-stack>
            </s-clickable>
            <s-clickable
              padding="base"
              borderWidth="base"
              borderRadius="base"
              onClick={() => setRoleChoice("secondary")}
            >
              <s-stack gap="small">
                <s-stack direction="inline" gap="small" alignItems="center">
                  <s-text type="strong">Secondary store</s-text>
                  {roleChoice === "secondary" ? (
                    <s-badge tone="success">Selected</s-badge>
                  ) : null}
                </s-stack>
                <s-paragraph color="subdued">
                  This store joins my main store, which manages where visitors
                  go.
                </s-paragraph>
              </s-stack>
            </s-clickable>
            <s-button
              variant="primary"
              onClick={() => {
                if (roleChoice === "secondary") {
                  setRolePicked(true);
                  setCodeInput("");
                  return;
                }
                try {
                  localStorage.setItem("sg_store_role_picked", "1");
                } catch {
                  /* ignore */
                }
                setRolePicked(true);
              }}
            >
              Continue
            </s-button>
          </s-stack>
        </s-section>
        
      </SafePage>
    );
  }

  // After picking Secondary on Multi store that isn't linked yet - show join.
  if (
    unlocked &&
    !isSecondary &&
    rolePicked &&
    roleChoice === "secondary" &&
    !linkedAsSecondary
  ) {
    return (
      <SafePage
      screen="stores"
      heading="Store redirect"
      accessory={<PhaseBadge phase={phase} />}
      primaryAction={storesPrimary}
    >
        <s-section heading="Join your primary store">
          <s-paragraph color="subdued">
            Enter your primary store’s URL (custom domain or .myshopify.com).
          </s-paragraph>
          {err ? (
            <ErrorBanner heading="Couldn’t connect">{err}</ErrorBanner>
          ) : null}
          <s-text-field
            label="Your primary store’s URL"
            value={codeInput}
            onChange={(e: Event) => setCodeInput(fieldValue(e))}
            details="e.g. linenandco.com or brand.myshopify.com"
          />
          <s-box paddingBlockStart="base">
            <s-stack direction="inline" gap="base">
              <s-button
                variant="primary"
                disabled={busy || !codeInput.trim()}
                loading={busy}
                onClick={() => void run(() => joinPrimary(codeInput))}
              >
                Send request
              </s-button>
              <s-button
                variant="tertiary"
                onClick={() => {
                  setRoleChoice("primary");
                  try {
                    localStorage.setItem("sg_store_role_picked", "1");
                  } catch {
                    /* ignore */
                  }
                  setRolePicked(true);
                }}
              >
                This is my primary store
              </s-button>
            </s-stack>
          </s-box>
        </s-section>
        
      </SafePage>
    );
  }

  // —— 3) Primary Store (Multi-store / Scale) ——
  return (
    <SafePage
      screen="stores"
      heading="Store redirect"
      accessory={<PhaseBadge phase={phase} />}
      primaryAction={storesPrimary}
    >
      <SaveBarHost
        id={MODE_SAVE_ID}
        saving={busy}
        onSave={() => void saveMode()}
        onDiscard={() => setDraftAuto(null)}
      />
{err ? (
        <ErrorBanner heading="Couldn’t update">{err}</ErrorBanner>
      ) : null}
      <VisitorModeSection
        variant="store"
        marketsAuto={marketsAuto}
        free={false}
        busy={busy}
        onAsk={() => setDraftAuto(false)}
        onAuto={() => setDraftAuto(true)}
      />
      <WebsiteRulesSection atRulesMax={atRulesMax} />

      <s-section heading="My Shopify stores">
        <s-box padding="base" borderWidth="base" borderRadius="base">
          <s-stack direction="block" gap="small">
            <s-badge tone="success">Primary Store</s-badge>
            <s-text type="strong">{shopDomain || "This store"}</s-text>
            <s-paragraph color="subdued">
              This store manages your connected stores
              {storeLimit != null
                ? ` · up to ${storeLimit} on ${planName}`
                : ""}.
            </s-paragraph>
          </s-stack>
        </s-box>
        <s-box paddingBlockStart="base">
          <s-stack direction="inline" gap="base">
            <s-button
              variant="primary"
              disabled={busy || atStoreLimit}
              onClick={() => setShowConnectFlow(true)}
            >
              Connect another store
            </s-button>
            <s-button
              variant="tertiary"
              onClick={() => {
                const url = shopifyAppInstallUrl(APP_HANDLE);
                void navigator.clipboard?.writeText(url).then(
                  () => showToast("Install link copied"),
                  () => showToast(url),
                );
              }}
            >
              Copy install link
            </s-button>
            <s-button
              variant="tertiary"
              href={shopifyAppInstallMailto({
                appHandle: APP_HANDLE,
                primaryDomain: shopDomain,
              })}
            >
              Email me the steps
            </s-button>
            {network.length > 1 ? (
              <s-button
                variant="secondary"
                disabled={busy}
                loading={busy}
                onClick={() =>
                  void run(async () => {
                    const r = await publishToAllStores();
                    showToast(
                      `Published to ${r.ok_count ?? 0} connected store(s)`,
                    );
                  })
                }
              >
                Publish network
              </s-button>
            ) : null}
          </s-stack>
        </s-box>
        {network.length > 1 ? (
          <s-box paddingBlockStart="base">
            <s-section heading="Conflict check">
              <s-paragraph color="subdued">
                No conflicts. No visitor can be sent in a loop. Fix any country
                overlap between stores before publishing the network.
              </s-paragraph>
            </s-section>
          </s-box>
        ) : null}
        {atStoreLimit ? (
          <s-box paddingBlockStart="base">
            <WarningBanner heading="Store limit reached">
              Your {planName} plan allows {storeLimit} stores (including this
              Primary).{" "}
              {planName === "Multi-store" ? (
                <>
                  <AppLink to="/app/plans">Upgrade to Scale</AppLink> for up to
                  10.
                </>
              ) : (
                "Remove a connected store before adding another."
              )}
            </WarningBanner>
          </s-box>
        ) : null}
        {storeLimit != null && !atStoreLimit ? (
          <s-box paddingBlockStart="base">
            <s-paragraph color="subdued">
              You can connect up to {storeLimit} stores on your current plan
              ({connectedCount} of {storeLimit} used).
            </s-paragraph>
          </s-box>
        ) : null}
      </s-section>

      {network.some((r) => r.status === "pending") ? (
        <s-box paddingBlockEnd="base">
          <s-stack direction="inline" gap="base" alignItems="center">
            <s-paragraph color="subdued">
              Pending join requests wait for your approval.
            </s-paragraph>
            <s-button
              variant="primary"
              disabled={busy}
              loading={busy}
              onClick={() =>
                void run(async () => {
                  const r = await approveAllNetworkMembers();
                  showToast(
                    `Approved ${r.approved ?? 0} store${(r.approved ?? 0) === 1 ? "" : "s"}`,
                  );
                })
              }
            >
              Approve all
            </s-button>
          </s-stack>
        </s-box>
      ) : null}

      {network.some(
        (r) =>
          r.status === "active" &&
          r.role !== "primary" &&
          !(r.serves_countries?.length),
      ) ? (
        <s-box paddingBlockEnd="base">
          <InfoBanner heading="Choose countries for connected stores">
            A store just connected. Assign which countries go there, then
            Publish to connected stores.
          </InfoBanner>
        </s-box>
      ) : null}

      {showConnectFlow ? (
        <s-section heading="Connect another store">
          <s-paragraph>
            On the other Shopify store, open SafeGeo → Store redirect → join as
            a secondary store and paste this store’s URL (
            <s-text type="strong">{shopDomain || "your .myshopify.com domain"}</s-text>
            ). Same-owner stores connect automatically.
          </s-paragraph>
          <s-stack direction="inline" gap="base">
            <s-button
              variant="primary"
              onClick={() => {
                const url = shopifyAppInstallUrl(APP_HANDLE);
                void navigator.clipboard?.writeText(url).then(
                  () => showToast("Install link copied"),
                  () => showToast(url),
                );
              }}
            >
              Copy install link
            </s-button>
            <s-button
              variant="tertiary"
              onClick={() => setShowConnectFlow(false)}
            >
              Done
            </s-button>
          </s-stack>
        </s-section>
      ) : null}

      <s-section heading="Connected stores">
        {network.length <= 1 && rows.length <= 1 ? (
          <s-paragraph color="subdued">
            No other stores connected yet. Use{" "}
            <s-text type="strong">Connect another store</s-text> to invite your
            .co.uk (or other) shop.
          </s-paragraph>
        ) : null}
        <s-table>
          <s-table-header-row>
            <s-table-header listSlot="primary">Store</s-table-header>
            <s-table-header>Storefront URL</s-table-header>
            <s-table-header>Serves countries</s-table-header>
            <s-table-header>Role</s-table-header>
            <s-table-header>Status</s-table-header>
            <s-table-header>App embed</s-table-header>
          </s-table-header-row>
          <s-table-body>
            {rows.map((row) => {
              const badge = statusBadge(String(row.status || ""));
              const emb = embedBadge(row);
              const isSelfPrimary =
                row.role === "primary" || row.shop_id === "self";
              const url = defaultStorefrontUrl(
                row.shop_domain,
                row.storefront_url,
              );
              return (
                <s-table-row key={row.shop_id}>
                  <s-table-cell>{row.shop_domain}</s-table-cell>
                  <s-table-cell>
                    <s-text color="subdued">{url || "—"}</s-text>
                  </s-table-cell>
                  <s-table-cell>
                    <s-stack direction="block" gap="small">
                      <s-text>
                        {isSelfPrimary
                          ? "Primary - manages routing"
                          : row.serves_countries?.length
                            ? row.serves_countries
                                .map((c) => countryName(c))
                                .join(", ")
                            : "Not set yet"}
                      </s-text>
                      {!isSelfPrimary ? (
                        <s-button
                          variant={
                            row.status === "active" &&
                            !(row.serves_countries?.length)
                              ? "primary"
                              : "tertiary"
                          }
                          onClick={() => {
                            setEditId(row.shop_id);
                            setServesDraft(row.serves_countries || []);
                            setUrlDraft(
                              defaultStorefrontUrl(
                                row.shop_domain,
                                row.storefront_url,
                              ),
                            );
                          }}
                        >
                          {row.status === "active" &&
                          !(row.serves_countries?.length)
                            ? "Choose countries"
                            : "Edit countries & URL"}
                        </s-button>
                      ) : null}
                    </s-stack>
                  </s-table-cell>
                  <s-table-cell>
                    {isSelfPrimary ? "Primary Store" : "Connected"}
                  </s-table-cell>
                  <s-table-cell>
                    <s-stack
                      direction="inline"
                      gap="small"
                      alignItems="center"
                    >
                      <s-badge tone={badge.tone}>{badge.label}</s-badge>
                      {row.status === "pending" ? (
                        <>
                          <s-button
                            variant="primary"
                            disabled={busy}
                            onClick={() =>
                              void run(async () => {
                                await approveNetworkMember(row.shop_id);
                              }, "Store approved and connected")
                            }
                          >
                            Approve
                          </s-button>
                          <s-button
                            variant="tertiary"
                            disabled={busy}
                            onClick={() =>
                              void run(async () => {
                                await declineNetworkMember(row.shop_id);
                              }, "Request declined")
                            }
                          >
                            Decline
                          </s-button>
                        </>
                      ) : null}
                    </s-stack>
                  </s-table-cell>
                  <s-table-cell>
                    <s-badge tone={emb.tone}>{emb.label}</s-badge>
                  </s-table-cell>
                </s-table-row>
              );
            })}
          </s-table-body>
        </s-table>
        <s-box paddingBlockStart="base">
          <s-paragraph>
            <s-text type="strong">Everyone else goes to</s-text> This store
          </s-paragraph>
          <s-paragraph color="subdued">
            Countries without a store assignment stay here. Assign countries on
            each connected store above to route them.
          </s-paragraph>
        </s-box>
      </s-section>

      {editId ? (
        <s-section
          heading="Send shoppers to this store"
          subheading="Countries go here · storefront URL is where SafeGeo sends them."
        >
          <CountryMultiSelect
            label="Countries this store serves"
            value={servesDraft}
            onChange={setServesDraft}
            disabled={busy}
          />
          <s-box paddingBlockStart="base">
            <s-url-field
              label="Storefront URL"
              value={urlDraft}
              onChange={(e: Event) => setUrlDraft(fieldValue(e))}
              details="Auto-filled from the connected shop. Change if you use a custom domain (e.g. https://yourbrand.co.uk)."
            />
          </s-box>
          <s-box paddingBlockStart="base">
            <s-stack direction="inline" gap="base">
              <s-button
                variant="primary"
                disabled={busy}
                loading={busy}
                onClick={() =>
                  void run(async () => {
                    await assignStoreServes({
                      shop_id: editId,
                      serves_countries: servesDraft,
                      storefront_url: urlDraft || undefined,
                    });
                    try {
                      await publishToAllStores();
                      showToast(
                        "Countries and URL saved and published",
                      );
                    } catch {
                      showToast(
                        "Saved - use Publish to connected stores to go live",
                      );
                    }
                    setEditId(null);
                  })
                }
              >
                Save & publish
              </s-button>
              <s-button variant="tertiary" onClick={() => setEditId(null)}>
                Cancel
              </s-button>
            </s-stack>
          </s-box>
        </s-section>
      ) : null}

      <PreviewAsVisitor
        context="store"
        prefer={status?.settings?.market_countries || undefined}
      />
    </SafePage>
  );
}
